import { useCallback, useEffect, useRef, useState } from 'react';
import mpegts from 'mpegts.js';
import useControls from "../shared/useControls.js";
import IdleGuard from "./IdleGuard.js";
mpegts.LoggingControl.applyConfig({
  enableError: true,
  enableWarn: true,
  enableInfo: true
});
export default function usePlayer(opts) {
  const {
    url,
    getAuthHeader,
    delay = 5000
  } = opts;
  /**
   * @type {import('react').Ref<HTMLDivElement>}
   */
  const containerRef = useRef();
  /**
   * @type {import('react').Ref<HTMLVideoElement>}
   */
  const videoRef = useRef();
  /**
   * @type {import('react').Ref<mpegts.Player>}
   */
  const playerRef = useRef();
  const destroyTimerRef = useRef();
  const isLoadedRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [error, setError] = useState();
  const createPlayer = useCallback(() => {
    setError(null);
    const headers = getAuthHeader ? {
      Authorization: getAuthHeader()
    } : undefined;
    const player = mpegts.createPlayer({
      type: 'flv',
      isLive: true,
      url,
      hasAudio: false,
      cors: true
    }, {
      autoCleanupSourceBuffer: true,
      enableWorker: true,
      liveSync: true,
      headers
    });
    player.on(mpegts.Events.ERROR, (errorType, errorDetails, err) => {
      console.error(new Error(err.message));
      playerRef.current.pause();
      setPlaying(false);
      playerRef.current.unload();
      isLoadedRef.current = false;
      playerRef.current.destroy();
      playerRef.current = null;
      setError({
        type: errorType,
        message: errorDetails,
        original: err
      });
    });
    player.attachMediaElement(videoRef.current);
    return player;
  }, [url, getAuthHeader]);
  const onIdle = useCallback(() => {
    playerRef.current?.pause();
    setPlaying(false);
    playerRef.current?.unload();
    isLoadedRef.current = false;
    playerRef.current?.destroy();
    playerRef.current = null;
  }, []);
  useEffect(() => {
    if (playing === true) {
      IdleGuard.start({
        onIdle
      });
    } else if (playing === false) {
      // eslint-disable-next-line no-underscore-dangle
      IdleGuard.stop();
    }
  }, [onIdle, playing]);
  useEffect(() => {
    playerRef.current = createPlayer();
    return () => {
      playerRef.current?.destroy();
      playerRef.current = null;
    };
  }, [createPlayer]);
  return {
    controls: useControls({
      containerRef,
      createPlayer,
      delay,
      destroyTimerRef,
      isLoadedRef,
      playerRef,
      videoRef,
      loading,
      setLoading,
      playing,
      setPlaying
    }),
    player: playerRef.current,
    ref: videoRef,
    containerRef,
    error
  };
}